import { motion } from "framer-motion";
import { Instagram, Mail, MapPin } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import { EASE } from "@/components/Reveal";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

export default function ComingSoon() {
  return (
    <section
      data-testid="coming-soon-page"
      className="relative flex min-h-svh items-center justify-center overflow-hidden bg-navy px-6 py-16"
    >
      <img
        src="/images/hero-creek.jpg"
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover opacity-50"
      />
      <div aria-hidden className="absolute inset-0 bg-navy/60 mix-blend-multiply" />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-transparent to-navy"
      />

      <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center text-center">
        <motion.img
          src="/images/logo.png"
          alt="Totier Creek Candle Co. bird emblem"
          data-testid="coming-soon-logo"
          className="h-24 w-24 sm:h-28 sm:w-28"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.15, ease: EASE }}
        />

        <motion.div {...fadeUp(0.35)}>
          <span
            data-testid="coming-soon-badge"
            className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-amber/60 px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.35em] text-amber"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber" />
            Coming Soon
          </span>
        </motion.div>

        <h1 className="mt-7 font-playfair text-cream">
          <MaskedLine
            delay={0.5}
            className="text-5xl leading-[0.95] tracking-tight sm:text-7xl"
          >
            Totier Creek
          </MaskedLine>
          <MaskedLine
            delay={0.65}
            className="text-5xl leading-[0.95] tracking-tight sm:text-7xl"
          >
            <span className="italic text-cream/90">Candle Co.</span>
          </MaskedLine>
        </h1>

        <motion.p
          {...fadeUp(0.85)}
          className="mt-6 text-[11px] font-medium uppercase tracking-[0.45em] text-amber sm:text-xs"
        >
          Esmont, Virginia · Est. 2026
        </motion.p>

        <motion.p
          {...fadeUp(0.95)}
          data-testid="coming-soon-message"
          className="mt-6 max-w-md text-sm leading-loose text-cream/80 sm:text-base"
        >
          We're getting ready. Leave your email and we'll let you know when
          the site opens.
        </motion.p>

        <motion.div {...fadeUp(1.1)} className="mt-8 w-full max-w-xl">
          <div className="flex justify-center">
            <NewsletterForm
              source="coming-soon"
              buttonLabel="Notify Me at Launch"
              microcopy="One email when we launch · No spam · Unsubscribe anytime"
            />
          </div>
        </motion.div>

        <motion.p
          {...fadeUp(1.25)}
          className="mt-10 font-playfair text-base italic text-cream/60"
        >
          “Be still, and know that I am God.” — Psalm 46:10
        </motion.p>

        <motion.div
          {...fadeUp(1.35)}
          className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] font-medium uppercase tracking-[0.25em] text-cream/60"
        >
          <a
            href="mailto:totiercreekcandleco@gmail.com"
            data-testid="coming-soon-email"
            className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-amber"
          >
            <Mail size={14} className="text-amber" /> totiercreekcandleco@gmail.com
          </a>
          <a
            href="https://instagram.com/totiercreekcandleco"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coming-soon-instagram"
            className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-amber"
          >
            <Instagram size={14} className="text-amber" /> @totiercreekcandleco
          </a>
          <span className="inline-flex items-center gap-2">
            <MapPin size={14} className="text-amber" /> Esmont, Virginia
          </span>
        </motion.div>

        <motion.p
          {...fadeUp(1.45)}
          className="mt-12 text-[10px] uppercase tracking-[0.3em] text-cream/35"
        >
          © 2026 Totier Creek Candle Co.
        </motion.p>
      </div>
    </section>
  );
}
